import { Link, type LinkProps } from '@tanstack/react-router'
import { ArrowLeftCircleIcon } from 'lucide-react'

type BackLinkProps = (Omit<LinkProps, 'className'> & { children: React.ReactNode }) | { children: React.ReactNode }

/**
 * Back navigation link with optional history-based navigation.
 *
 * Usage:
 * - With `to` prop: navigates to the specified route (use for explicit destinations)
 * - Without `to` prop: uses history.back() to preserve URL state (search params, etc.)
 */
export function BackLink({ children, ...props }: BackLinkProps) {
  if (!('to' in props) && !('href' in props)) {
    const handleBack = () => {
      // Not every supported browser has view transitions yet (the DOM typings
      // claim it is always there).
      if ('startViewTransition' in document) {
        document.startViewTransition(() => window.history.back())
      } else {
        window.history.back()
      }
    }

    return (
      <button onClick={handleBack} className="text-red mb-6 inline-flex cursor-pointer items-center gap-1.5 underline">
        <ArrowLeftCircleIcon className="mt-0.5 h-5 w-5" />
        {children}
      </button>
    )
  }

  return (
    <Link {...props} className="text-red mb-6 inline-flex items-center gap-1.5 underline">
      <ArrowLeftCircleIcon className="mt-0.5 h-5 w-5" />
      {children}
    </Link>
  )
}
